import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import FormGroup from "../../../Components/base/forms/FormGroup";
import Input from "../../../Components/base/forms/Input";
import SearchableSelect from "../../../Components/base/forms/SearchableSelect";
import Select from "../../../Components/base/forms/Select";
import Modal from "../../../Components/base/Modal/Modal";
import {
  addWebMenu,
  getViewWebMenu,
  getWebMenuData,
  updateWebMenu,
} from "../../../Services/WebMenuServices";

const WebMenuModal = ({
  isOpen,
  setIsOpen,
  itemData = {},
  webMenuData = [],
  fromSource = "",
}) => {
  const hasItemData = Object.keys(itemData).length > 0;
  const dispatch = useDispatch();

  const parentMenuArray = [
    { value: null, text: "Main Menu" },
    ...webMenuData
      .map((menu) => ({
        value: menu.id,
        text: menu.name,
      })),
  ];

  const hideMenuOptions = [
    { value: 0, label: "No" },
    { value: 1, label: "Yes" },
  ];

  const statusOptions = [
    { value: "active", label: "active" },
    { value: "inactive", label: "inactive" },
  ];

  const formik = useFormik({
    enableReinitialize: true, // ✅ Allow reinitialization when initialValues change
    initialValues: {
      menuTitle: itemData?.name || "",
      parentMenu: itemData?.parent_name || "", // fallback to "Main Menu"
      parentMenuId: itemData?.parent_id ?? null,
      path: itemData?.path || "",
      orderNo: itemData?.order_no || "",
      iconName: itemData?.icon_name || "",
      hideMenu: itemData?.is_hide ?? 0,
      status: itemData?.status ?? "active",
    },
    validate: (values) => {
      const errors = {};

      if (values.menuTitle === "") {
        errors.menuTitle = "Please enter menu title.";
      }
      if (values.orderNo === "") {
        errors.orderNo = "Please enter order number.";
      }
      if (values.path === "") {
        errors.path = "Please enter menu path.";
      }
      if (values.status === "") {
        errors.status = "Please select status.";
      }
      return errors;
    },
    validateOnChange: true,
    onSubmit: (values, { resetForm }) => {
      handleSubmit(values);
      setIsOpen(false);
      resetForm();
    },
  });

  const handleSubmit = async (values) => {
    try {
      const requestBody = {
        name: values.menuTitle,
        path: values.path,
        icon_name: values.iconName,
        order_no: values.orderNo,
        parent_id: values.parentMenuId,
        is_hide: values.hideMenu,
        status: values.status,
      };

      let response;
      if (hasItemData) {
        response = await dispatch(updateWebMenu(requestBody, itemData.id));
      } else {
        response = await dispatch(addWebMenu(requestBody));
      }
      if (response.status_code === 200) {
        toast.success(response.message);
        fromSource === "webMenu"
          ? dispatch(getWebMenuData())
          : dispatch(getViewWebMenu(itemData.parent_id));
      } else {
        toast.error(
          response.message || "Web Menu add failed. Please try again.",
        );
        console.error("Error web menu add", response);
      }
    } catch (error) {
      console.error("API call error", error);
    }
  };

  /** Handle Methods Start */
  const handleParentMenuChange = (e) => {
    const selectedValue = e.target.value;
    const selectedMenu = parentMenuArray.find(
      (menu) => menu.value === selectedValue,
    );
    formik.setFieldValue("parentMenu", selectedValue);
    formik.setFieldValue("parentMenuId", selectedMenu?.id || "");
  };

  return (
    <Modal
      setIsOpen={setIsOpen}
      isOpen={isOpen}
      id="addWebMenu"
      title={hasItemData ? "Edit Web Menu" : "Add Web Menu"}
      onSubmit={formik.handleSubmit}
      size={"lg"}
    >
      <div className="row g-4">
        <FormGroup id="menuTitle" label="Menu Title" className="col-md-6">
          <Input
            name="menuTitle"
            placeholder="Enter Menu Title"
            onChange={formik.handleChange}
            value={formik.values.menuTitle}
            onBlur={formik.handleBlur}
            isValid={formik.isValid}
            isTouched={formik.touched.menuTitle}
            invalidFeedback={formik.errors.menuTitle}
            required
          />
        </FormGroup>
        <FormGroup id="parentMenu" label="Parent Menu" className="col-md-6">
          <div className="position-relative">
            <SearchableSelect
              name="parentMenu"
              list={parentMenuArray}
              placeholder="Select Parent Menu"
              // onChange={(e) => handleParentMenuChange(e)}
              onBlur={formik.handleBlur}
              value={formik.values.parentMenuId}
              onChange={(e) =>
                formik.setFieldValue("parentMenuId", e.target.value)
              }
              search={true}
              isValid={formik.isValid}
              error={formik.errors.parentMenu}
              touched={formik.touched.parentMenu}
            />
          </div>
        </FormGroup>
        <FormGroup id="path" label="Menu Path" className="col-md-12">
          <Input
            name="path"
            placeholder="Enter Menu Path"
            onChange={formik.handleChange}
            value={formik.values.path}
            onBlur={formik.handleBlur}
            isValid={formik.isValid}
            isTouched={formik.touched.path}
            invalidFeedback={formik.errors.path}
            required
          />
        </FormGroup>
        <FormGroup id="iconName" label="Menu Icon" className="col-md-6">
          <Input
            name="iconName"
            placeholder="Enter menu icon"
            onChange={formik.handleChange}
            value={formik.values.iconName}
            onBlur={formik.handleBlur}
            isValid={formik.isValid}
          />
        </FormGroup>
        <FormGroup id="orderNo" label="Menu Order" className="col-md-6">
          <Input
            name="orderNo"
            placeholder="Enter menu order"
            onChange={formik.handleChange}
            value={formik.values.orderNo}
            onBlur={formik.handleBlur}
            isValid={formik.isValid}
            type="number"
            isTouched={formik.touched.orderNo}
            invalidFeedback={formik.errors.orderNo}
            required
          />
        </FormGroup>
        <FormGroup label="Is Menu Hide" className="col-md-6">
          <Select
            name="menuHide"
            list={hideMenuOptions}
            placeholder="Select menu status"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.menuHide}
          />
        </FormGroup>
        <FormGroup label="Status" className="col-md-6">
          <Select
            name="status"
            list={statusOptions}
            placeholder="Select Status"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.status || ""}
            required
            error={formik.errors.status}
            touched={formik.touched.status}
          />
        </FormGroup>
      </div>
    </Modal>
  );
};

export default WebMenuModal;